import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { Paperclip, X, FileText, Image as ImageIcon, Loader2, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,application/pdf";
const MAX_MB = 5;

/**
 * FileUpload — single-file receipt/document uploader with live progress.
 * Props:
 *   value      — current attachment object { id, url, filename, size, content_type } or null
 *   onChange   — (attachment|null) => void
 *   testId     — base data-testid for inputs/buttons
 *   label      — label text (default: "Receipt / supporting doc")
 *   onScan     — optional (parsed) => void. When set, exposes a "Scan with AI" button
 *                (only for image attachments) that calls POST /api/attachments/{id}/scan
 *                and yields the parsed OCR fields to the parent for auto-fill.
 */
export default function FileUpload({ value, onChange, testId = "file-upload", label = "Receipt / supporting doc (optional)", onScan }) {
    const inputRef = useRef(null);
    const [progress, setProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [localName, setLocalName] = useState(value?.filename || "");

    useEffect(() => { setLocalName(value?.filename || ""); }, [value]);

    const pick = () => inputRef.current?.click();

    const handleFile = async (file) => {
        if (!file) return;
        if (file.size > MAX_MB * 1024 * 1024) {
            toast.error(`File too large (max ${MAX_MB} MB)`);
            return;
        }
        const okType = ACCEPT.split(",").includes(file.type);
        if (!okType) {
            toast.error("Unsupported file type. Allowed: JPG, PNG, WEBP, HEIC, HEIF, PDF.");
            return;
        }
        setLocalName(file.name);
        setUploading(true);
        setProgress(0);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const orgId = localStorage.getItem("bl_org_id");
            const { data } = await axios.post(`${API_BASE}/attachments`, fd, {
                withCredentials: true,
                headers: { ...(orgId ? { "X-Org-Id": orgId } : {}) },
                onUploadProgress: (evt) => {
                    if (!evt.total) return;
                    setProgress(Math.round((evt.loaded / evt.total) * 100));
                },
            });
            setProgress(100);
            onChange?.(data);
            toast.success("Receipt attached");
        } catch (e) {
            toast.error(formatApiError(e));
            setLocalName(value?.filename || "");
        } finally {
            setUploading(false);
        }
    };

    const clear = () => {
        onChange?.(null);
        setProgress(0);
        setLocalName("");
        if (inputRef.current) inputRef.current.value = "";
    };

    const scan = async () => {
        if (!value?.id) return;
        setScanning(true);
        try {
            const { data } = await api.post(`/attachments/${value.id}/scan`);
            onScan?.(data.parsed || {});
            toast.success("Receipt scanned — fields pre-filled");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setScanning(false);
        }
    };

    const isImage = (value?.content_type || "").startsWith("image/");
    const Icon = value ? (isImage ? ImageIcon : FileText) : Paperclip;
    const canScan = !!(onScan && value?.id && isImage);

    return (
        <div data-testid={`${testId}-root`}>
            <div className="text-sm font-medium text-ocean-700 mb-1.5">{label}</div>
            <input
                ref={inputRef}
                type="file"
                accept={ACCEPT}
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid={`${testId}-input`}
            />
            {!value && !uploading && (
                <button
                    type="button"
                    onClick={pick}
                    className="w-full rounded-2xl border-2 border-dashed border-sand-300 bg-sand-50/50 hover:bg-sand-100 hover:border-ocean-700/50 transition-colors px-4 py-3 flex items-center justify-center gap-2 text-sm text-muted-foreground"
                    data-testid={`${testId}-pick`}
                >
                    <Paperclip className="h-4 w-4" />
                    Attach receipt (JPG, PNG, WEBP, HEIC, PDF · max {MAX_MB} MB)
                </button>
            )}
            {uploading && (
                <div className="rounded-2xl bg-sand-100 px-4 py-3" data-testid={`${testId}-progress`}>
                    <div className="flex items-center gap-2 text-sm text-ocean-700">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="truncate flex-1">{localName}</span>
                        <span className="font-mono text-xs text-muted-foreground">{progress}%</span>
                    </div>
                    <div className="mt-2 h-1.5 bg-sand-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-ocean-700 transition-all duration-150"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>
            )}
            {value && !uploading && (
                <div className="space-y-2" data-testid={`${testId}-attached-wrap`}>
                    <div className="rounded-2xl bg-secondary/10 border border-secondary/30 px-4 py-3 flex items-center gap-3" data-testid={`${testId}-attached`}>
                        <div className="h-9 w-9 rounded-xl bg-white grid place-items-center text-secondary-dark">
                            <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-ocean-700 truncate">{value.filename}</div>
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                                <CheckCircle2 className="h-3 w-3 text-secondary-dark" />
                                Attached · {(value.size / 1024).toFixed(0)} KB
                                {value.original_size && value.original_size > value.size ? (
                                    <span> (compressed from {(value.original_size / 1024).toFixed(0)} KB)</span>
                                ) : null}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={clear}
                            className="h-8 w-8 grid place-items-center rounded-xl text-muted-foreground hover:bg-white"
                            data-testid={`${testId}-remove`}
                            title="Remove"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    {canScan && (
                        <button
                            type="button"
                            onClick={scan}
                            disabled={scanning}
                            className="w-full rounded-2xl border border-ocean-700/20 bg-white hover:bg-ocean-700 hover:text-white hover:border-ocean-700 transition-colors px-4 py-2.5 flex items-center justify-center gap-2 text-sm font-medium text-ocean-700 disabled:opacity-60"
                            data-testid={`${testId}-scan`}
                        >
                            {scanning ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Reading receipt…
                                </>
                            ) : (
                                <>
                                    <Sparkles className="h-4 w-4" />
                                    Scan with AI · auto-fill from receipt
                                </>
                            )}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
